import { useEffect, useRef, useState } from "react";

const canHover = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/* Videos whose owner has embedding disabled — never retried. */
const blocked = new Set();

const destroyPlayer = (playerRef) => {
  const instance = playerRef.current;
  playerRef.current = null;
  if (!instance) return;
  try {
    instance.destroy();
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("YouTube player cleanup skipped", error);
    }
  }
};

let apiPromise = null;
const loadApi = () => {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
    if (!document.getElementById("yt-iframe-api")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    }
  });
  return apiPromise;
};

/* Full featured player — created on user click, so sound is allowed.
   Reports embed failures so the caller can fall back to the poster. */
export const FeaturedPlayer = ({ id, onFail }) => {
  const host = useRef(null);
  const player = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadApi().then((YT) => {
      if (cancelled || !host.current) return;
      player.current = new YT.Player(host.current, {
        videoId: id,
        host: "https://www.youtube-nocookie.com",
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 1,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
        },
        events: {
          onReady: (e) => e.target.playVideo(),
          onError: () => !cancelled && onFail?.(),
        },
      });
    });
    return () => {
      cancelled = true;
      destroyPlayer(player);
    };
  }, [id, onFail]);

  return (
    <div
      data-testid="showreel-player"
      className="absolute inset-0 h-full w-full"
    >
      <div ref={host} className="h-full w-full" />
    </div>
  );
};

/* Muted preview built on the YouTube IFrame Player API.
   Created on hover, destroyed on leave (which resets playback and frees the
   player). Falls back silently to the still if embedding is disabled. */
export const VideoPreview = ({ id, active, vertical }) => {
  const host = useRef(null);
  const player = useRef(null);
  const [hoverable] = useState(canHover);
  const [mount, setMount] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hoverable || !id || blocked.has(id)) return undefined;
    if (active) {
      setMount(true);
      return undefined;
    }
    setVisible(false);
    const t = setTimeout(() => setMount(false), 300);
    return () => clearTimeout(t);
  }, [active, hoverable, id]);

  useEffect(() => {
    if (!mount || !id) return undefined;
    let cancelled = false;

    loadApi().then((YT) => {
      if (cancelled || !host.current) return;
      player.current = new YT.Player(host.current, {
        videoId: id,
        host: "https://www.youtube-nocookie.com",
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          loop: 1,
          playlist: id,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
        },
        events: {
          onReady: (e) => {
            e.target.mute();
            e.target.playVideo();
          },
          onStateChange: (e) => {
            if (e.data === 1 && !cancelled) setVisible(true);
          },
          onError: () => {
            blocked.add(id);
            if (!cancelled) {
              setVisible(false);
              setMount(false);
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      destroyPlayer(player);
    };
  }, [mount, id]);

  if (!mount || !id) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="hover-preview"
      className="pointer-events-none absolute inset-0 overflow-hidden transition-opacity duration-500"
      style={{ opacity: visible ? 1 : 0 }}
    >
      <div
        className={
          vertical
            ? "absolute left-1/2 top-1/2 aspect-video w-[316%] -translate-x-1/2 -translate-y-1/2"
            : "absolute inset-0 h-full w-full"
        }
      >
        <div ref={host} className="h-full w-full" />
      </div>
    </div>
  );
};
